import { Controller } from "@hotwired/stimulus"
import { formatDuration, safeHttpsUrl, searchHeading } from "../lib/ig_music"

// 楽曲選択モーダル(予約投稿フォームの外に置く)。
// ig-music:open-picker で開き、選んだ曲を ig-music-picker:select(detail: { track })で通知する
export default class extends Controller {
  static targets = ["dialog", "input", "heading", "status", "list"]
  static values = { searchUrl: String }

  connect() {
    this.timer = null
    this.requestSeq = 0
    this.tracks = []
    this.loaded = false
    this.player = new Audio()
    this.playingIndex = null
  }

  disconnect() {
    clearTimeout(this.timer)
    this.stopPreview()
  }

  open() {
    this.dialogTarget.showModal()
    if (!this.loaded) {
      this.loaded = true
      this.search("")
    }
    this.inputTarget.focus()
  }

  close() {
    this.dialogTarget.close()
  }

  // dialog の close(ESC を含む)
  closed() {
    this.stopPreview()
  }

  keydown(event) {
    if (event.key === "Enter") event.preventDefault()
  }

  input() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.search(this.inputTarget.value.trim()), 300)
  }

  async search(query) {
    const seq = ++this.requestSeq
    this.headingTarget.textContent = searchHeading(query)
    this.showStatus("読み込み中…")
    this.renderTracks([], { keepStatus: true })
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      if (query) url.searchParams.set("q", query)
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await response.json()
      if (seq !== this.requestSeq) return
      if (data.error) {
        this.searchFailed(query)
        this.showStatus(data.error)
        return
      }
      this.renderTracks(data.tracks || [])
    } catch {
      if (seq !== this.requestSeq) return
      this.searchFailed(query)
      this.showStatus("楽曲を取得できませんでした。時間をおいて再度お試しください")
    }
  }

  // トレンド(初回表示)の取得に失敗したら、モーダルを開き直したときに取り直す
  searchFailed(query) {
    if (!query) this.loaded = false
  }

  renderTracks(tracks, { keepStatus = false } = {}) {
    this.stopPreview()
    this.tracks = tracks
    this.listTarget.replaceChildren(...tracks.map((track, index) => this.row(track, index)))
    if (keepStatus) return
    if (tracks.length === 0) this.showStatus("該当する楽曲が見つかりませんでした")
    else this.showStatus(null)
  }

  showStatus(message) {
    this.statusTarget.textContent = message || ""
    this.statusTarget.classList.toggle("hidden", !message)
  }

  // 外部(Meta カタログ)のデータなので createElement / textContent だけで組み立てる
  row(track, index) {
    const li = document.createElement("li")
    li.className = "flex items-center gap-3 py-2"

    const artworkUrl = safeHttpsUrl(track.artwork_url)
    if (artworkUrl) {
      const img = document.createElement("img")
      img.src = artworkUrl
      img.alt = ""
      img.loading = "lazy"
      img.className = "w-10 h-10 rounded object-cover shrink-0"
      li.append(img)
    } else {
      const icon = document.createElement("span")
      icon.className = "w-10 h-10 rounded bg-base-200 flex items-center justify-center shrink-0"
      const glyph = document.createElement("span")
      glyph.className = "iconify lucide--music size-4 text-base-content/50"
      icon.append(glyph)
      li.append(icon)
    }

    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    const title = document.createElement("p")
    title.className = "text-sm font-semibold truncate"
    title.textContent = track.title
    // アーティスト名が長くても長さは切らないよう、アーティストだけ truncate する
    const meta = document.createElement("p")
    meta.className = "flex min-w-0 text-xs text-base-content/60"
    const artist = document.createElement("span")
    artist.className = "truncate"
    artist.textContent = track.artist || ""
    const duration = document.createElement("span")
    duration.className = "shrink-0"
    const durationText = formatDuration(track.duration_ms)
    // 区切りは「 ・ 」。flex の子の先頭の空白は詰められるため、ノーブレークスペースで挟む
    duration.textContent = track.artist && durationText ? `\u00a0・\u00a0${durationText}` : durationText
    meta.append(artist, duration)
    info.append(title, meta)
    li.append(info)

    const previewUrl = safeHttpsUrl(track.preview_url)
    const instagramUrl = safeHttpsUrl(track.instagram_preview_url)
    if (previewUrl) {
      const play = document.createElement("button")
      play.type = "button"
      play.className = "btn btn-ghost btn-sm sm:btn-xs btn-circle"
      play.dataset.index = String(index)
      play.dataset.action = "click->ig-music-picker#togglePreview"
      play.dataset.previewButton = "true"
      // 停止時に aria-label を戻すため曲名を持たせる(属性値として扱うだけで HTML には解釈させない)
      play.dataset.title = track.title || ""
      play.setAttribute("aria-label", `${track.title || ""} を試聴`)
      const playIcon = document.createElement("span")
      playIcon.className = "iconify lucide--play size-4"
      playIcon.setAttribute("aria-hidden", "true")
      play.append(playIcon)
      li.append(play)
    } else if (instagramUrl) {
      const link = document.createElement("a")
      link.href = instagramUrl
      link.target = "_blank"
      link.rel = "noopener"
      link.className = "link link-primary text-xs whitespace-nowrap inline-flex items-center gap-1"
      link.setAttribute("aria-label", `${track.title || ""} を Instagram で試聴(新しいタブ)`)
      const linkIcon = document.createElement("span")
      linkIcon.className = "iconify lucide--external-link size-4"
      linkIcon.setAttribute("aria-hidden", "true")
      const linkLabel = document.createElement("span")
      linkLabel.className = "hidden sm:inline"
      linkLabel.textContent = "Instagramで試聴"
      link.append(linkIcon, linkLabel)
      li.append(link)
    }

    const select = document.createElement("button")
    select.type = "button"
    select.className = "btn btn-outline btn-sm sm:btn-xs"
    select.dataset.index = String(index)
    select.dataset.action = "click->ig-music-picker#select"
    select.textContent = "選択"
    li.append(select)
    return li
  }

  togglePreview(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (this.playingIndex === index) {
      this.stopPreview()
      return
    }
    this.stopPreview()
    const url = safeHttpsUrl(this.tracks[index]?.preview_url)
    if (!url) return
    this.player.src = url
    this.player.play().catch(() => this.stopPreview())
    this.playingIndex = index
    this.setPreviewButtonState(event.currentTarget, true)
  }

  stopPreview() {
    this.player?.pause()
    this.playingIndex = null
    if (!this.hasListTarget) return
    this.listTarget.querySelectorAll('[data-preview-button="true"]').forEach((button) => {
      this.setPreviewButtonState(button, false)
    })
  }

  // 再生中は停止アイコン + 強調表示にする
  setPreviewButtonState(button, playing) {
    button.classList.toggle("btn-active", playing)
    button.classList.toggle("text-primary", playing)
    const icon = button.querySelector(".iconify")
    if (icon) icon.className = `iconify ${playing ? "lucide--square" : "lucide--play"} size-4`
    const title = button.dataset.title || ""
    button.setAttribute("aria-label", playing ? `${title} の試聴を止める` : `${title} を試聴`)
  }

  select(event) {
    const track = this.tracks[Number(event.currentTarget.dataset.index)]
    if (!track) return
    this.dispatch("select", { detail: { track } })
    this.close()
  }
}
